// @ts-nocheck

import { z } from "zod";
import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { WarningCircle as WarningCircleIcon, CheckCircle as CheckIcon } from "@phosphor-icons/react";

import { useSession } from "@/hooks/use-session";
import { api, mBaseURL } from "@/config/api";

export const Route = createFileRoute("/oauth/_layout/google")({
  validateSearch: z.object({
    code: z.string().nullish(),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useSearch();

  const session = useSession();
  const client = useQueryClient();
  const destination = "/dashboard/paid-media/connectors";

  const { mutate: createGoogleOAuthSession, status } = useMutation({
    mutationFn: async (code: string) => {
      const redirectUri = window.location.origin + "/auth/callback/google";
      const url = mBaseURL + "/api/oauth/google/authenticate?code=" + code + "&redirect_uri=" + encodeURIComponent(redirectUri);
      const response = await api(url, { method: "POST" });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw error;
      }
      return response.json();
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["google-accounts", session.organization.id] });
      client.invalidateQueries({ queryKey: ["google", "accounts"] });
      client.invalidateQueries({ queryKey: ["google", "ad-accounts"] });
      setTimeout(() => navigate({ to: destination }), 3000);
    },
    onError: () => {
      setTimeout(() => navigate({ to: destination }), 3000);
    },
  });

  useEffect(() => {
    if (params.code) {
      createGoogleOAuthSession(params.code);
      return;
    }

    setTimeout(() => navigate({ to: destination }), 3000);
  }, [params.code, createGoogleOAuthSession, navigate, destination]);

  if (!params.code) {
    return (
      <div className="flex flex-col items-center justify-center mx-auto max-w-md text-center text-balance">
        <WarningCircleIcon weight="fill" className="size-8 text-destructive" />
        <p className="text-base font-medium text-foreground mt-2">Failed to link your Google account</p>
        <p className="text-sm text-muted-foreground mt-0.5">We received an invalid code. You will be redirected back in 3 seconds...</p>
      </div>
    );
  }

  switch (status) {
    case "success":
      return (
        <div className="flex flex-col items-center justify-center max-w-md mx-auto text-center text-balance">
          <CheckIcon weight="fill" className="size-8 text-success" />
          <p className="text-sm text-foreground mt-2">Google account linked successfully. Redirecting...</p>
        </div>
      );

    case "error":
      return (
        <div className="flex flex-col items-center justify-center mx-auto max-w-md text-center text-balance">
          <WarningCircleIcon weight="fill" className="size-8 text-destructive" />
          <p className="text-base font-medium text-foreground mt-2">Failed to link your Google account</p>
          <p className="text-sm text-muted-foreground mt-0.5">We received an invalid code. You will be redirected back in 3 seconds...</p>
        </div>
      );

    default:
      return (
        <div className="flex items-center justify-center gap-3">
          <div className="spinner size-6 !border-foreground" />
          <p className="text-sm text-foreground">Linking your Google account...</p>
        </div>
      );
  }
}
